import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReactMarkdown from 'react-markdown';

import {
  ApiError,
  createNews,
  newsImageUrl,
  NewsPost,
  patchNews,
  uploadNewsImage,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';

type Mode =
  | { kind: 'create' }
  | { kind: 'edit'; post: NewsPost };

type Props = {
  open: boolean;
  mode: Mode;
  onClose: () => void;
  /** Parent updates its local posts list with the returned row so we
   *  don't need to refetch the whole feed after a save. */
  onSaved: (post: NewsPost) => void;
};

// `YYYY-MM-DD` slug from the date input + a kebab tail derived from
// the first ~5 body words. Best-effort suggestion — the field is
// fully editable. Returns '' when there's not enough to work with.
const suggestSlug = (date: string, body: string): string => {
  if (!date) return '';
  const tail = body
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .slice(0, 5)
    .join('-')
    .replace(/[^a-z0-9-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return tail ? `${date}-${tail}` : date;
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

// Max single-file upload — server caps at 5 MB after multipart decode,
// so reject anything obviously oversize before we burn the upload.
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const NewsEditDialog: React.FC<Props> = ({ open, mode, onClose, onSaved }) => {
  const { idToken } = useAuth();

  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [body, setBody] = useState('');
  // Stored as the on-the-wire shape: each entry is either a
  // fully-qualified Sirv URL (uploads done in this session) or a
  // legacy basename pulled off an existing post.
  const [images, setImages] = useState<string[]>([]);
  const [facebook, setFacebook] = useState('');

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  // Hidden file input — clicked imperatively by the "Add image" button
  // so we get an MUI-styled button instead of the native input chrome.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Seed the form on open. Effect runs whenever the dialog flips from
  // closed→open or the target post changes — closing without saving
  // discards local edits, which matches the dialog's "cancel" verb.
  useEffect(() => {
    if (!open) return;
    setError('');
    setSaving(false);
    setIdTouched(false);
    setShowPreview(false);
    if (mode.kind === 'edit') {
      setId(mode.post.id);
      setDate(mode.post.date);
      setBody(mode.post.body);
      setImages([...mode.post.images]);
      setFacebook(mode.post.facebook ?? '');
    } else {
      setId('');
      setDate(todayIso());
      setBody('');
      setImages([]);
      setFacebook('');
    }
  }, [open, mode]);

  // Auto-suggest the slug while the operator hasn't typed in the ID
  // field themselves. Editing the date or first sentence on a fresh
  // post refreshes the suggestion; once they touch the ID, we stop
  // overwriting their value.
  const suggested = useMemo(() => suggestSlug(date, body), [date, body]);
  useEffect(() => {
    if (mode.kind !== 'create') return;
    if (idTouched) return;
    setId(suggested);
  }, [suggested, idTouched, mode.kind]);

  const isCreate = mode.kind === 'create';
  const trimmedFb = facebook.trim();

  const idValid = /^[a-z0-9-]+$/i.test(id.trim());
  const canSave =
    !!idToken &&
    !!date &&
    body.trim().length > 0 &&
    id.trim().length > 0 &&
    idValid &&
    !saving &&
    !uploading;

  const handleSave = async () => {
    if (!idToken) return;
    setSaving(true);
    setError('');
    try {
      const fbPayload = trimmedFb.length > 0 ? trimmedFb : null;
      if (isCreate) {
        const created = await createNews(idToken, {
          id: id.trim(),
          date,
          body,
          images,
          facebook: fbPayload,
        });
        onSaved(created);
      } else {
        // Send only what actually changed so the server's
        // `updated_at` doesn't tick on no-op saves and the patch
        // stays small. `facebook: null` clears the existing link;
        // omitting the key leaves it.
        const original = mode.post;
        const patch: {
          date?: string;
          body?: string;
          images?: string[];
          facebook?: string | null;
        } = {};
        if (date !== original.date) patch.date = date;
        if (body !== original.body) patch.body = body;
        if (
          images.length !== original.images.length ||
          images.some((v, i) => v !== original.images[i])
        ) {
          patch.images = images;
        }
        const originalFb = original.facebook ?? '';
        if (trimmedFb !== originalFb) {
          patch.facebook = fbPayload;
        }
        const updated = await patchNews(idToken, original.id, patch);
        onSaved(updated);
      }
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setError(`A post with id "${id.trim()}" already exists.`);
        } else if (e.status === 400) {
          setError(`Validation failed: ${e.body || 'check the fields above.'}`);
        } else {
          setError(`Save failed (${e.status}).`);
        }
      } else {
        setError('Save failed — network error.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleImageRemove = (idx: number) => {
    setImages((arr) => arr.filter((_, i) => i !== idx));
  };

  const handleImagePicked = async (file: File | null | undefined) => {
    if (!file || !idToken) return;
    if (file.size > MAX_FILE_BYTES) {
      setError(
        `Image is ${(file.size / (1024 * 1024)).toFixed(1)} MB — max is ${MAX_FILE_BYTES / (1024 * 1024)} MB.`,
      );
      return;
    }
    setUploading(true);
    setError('');
    try {
      const { url } = await uploadNewsImage(idToken, file);
      setImages((arr) => [...arr, url]);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 413) setError('Image too large for the server.');
        else if (e.status === 415) setError('Unsupported image format.');
        else if (e.status === 503) setError('Image hosting (Sirv) not configured.');
        else setError(`Upload failed (${e.status}).`);
      } else {
        setError('Upload failed — network error.');
      }
    } finally {
      setUploading(false);
      // Reset the hidden input so re-uploading the same file fires
      // onChange again.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isCreate ? 'New post' : 'Edit post'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="ID (slug)"
            value={id}
            onChange={(e) => {
              setIdTouched(true);
              setId(e.target.value);
            }}
            disabled={!isCreate}
            helperText={
              isCreate
                ? 'Convention: YYYY-MM-DD-kebab-name. Auto-suggested from the date + first words of the body until you edit it.'
                : 'IDs are immutable. Delete + recreate if you need a different slug.'
            }
            error={id.length > 0 && !idValid}
            fullWidth
          />

          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 220 }}
          />

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2" sx={{ color: '#475569' }}>
                Body (markdown)
              </Typography>
              <Button
                size="small"
                onClick={() => setShowPreview((v) => !v)}
                sx={{ textTransform: 'none' }}
              >
                {showPreview ? 'Hide preview' : 'Show preview'}
              </Button>
            </Stack>
            <TextField
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              placeholder={
                'Markdown is supported: **bold**, *italic*, - bullet lists,\n[links](https://example.com), # headings, `code`.'
              }
              InputProps={{
                sx: { fontFamily: '"SFMono-Regular", Menlo, monospace', fontSize: '0.9rem' },
              }}
            />
            {showPreview && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                  color: '#1f2937',
                  '& p': { m: 0, mb: 1.5, '&:last-child': { mb: 0 } },
                  '& ul, & ol': { my: 1, pl: 3 },
                  '& li': { mb: 0.5 },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  '& a': { color: '#0f172a', textDecoration: 'underline' },
                  '& code': {
                    backgroundColor: '#f1f5f9',
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: '0.9em',
                  },
                }}
              >
                <ReactMarkdown>{body.replace(/\n+/g, '\n\n')}</ReactMarkdown>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ color: '#475569', mb: 1 }}>
              Images
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1.5 }}>
              Uploaded to Sirv (<code>News/</code>) and stored as full
              URLs. Only the first image renders on the home card; the
              full feed shows all of them.
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleImagePicked(e.target.files?.[0])}
            />
            {images.length === 0 && !uploading && (
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                No images yet.
              </Typography>
            )}
            <Stack spacing={1.5}>
              {images.map((img, idx) => (
                <Stack
                  key={`${img}-${idx}`}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    p: 1,
                    border: '1px solid #e5e7eb',
                    borderRadius: 1,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Box
                    component="img"
                    src={newsImageUrl(img)}
                    alt=""
                    sx={{
                      width: 80,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#fff',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#475569',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {img}
                    </Typography>
                    {idx === 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#0f766e', fontWeight: 600 }}
                      >
                        Primary (shown on home card)
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleImageRemove(idx)}
                    aria-label="Remove image"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Button
                size="small"
                startIcon={uploading ? null : <AddIcon />}
                disabled={uploading || !idToken}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: 'none' }}
              >
                {uploading ? 'Uploading…' : 'Add image'}
              </Button>
              {uploading && <CircularProgress size={18} />}
            </Stack>
          </Box>

          <TextField
            label="Facebook URL"
            value={facebook}
            onChange={(e) => setFacebook(e.target.value)}
            placeholder="https://www.facebook.com/..."
            helperText="Optional. Renders a small FB icon at the bottom-right of the card linking to the original post."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsEditDialog;
